<script setup>
import api from '@/services/api'
import { ref, watch } from 'vue'

const props = defineProps({
  itemDetalhes: { type: Object, required: true },
  listas: { type: Object, required: true },
  formatarValor: { type: Function, default: (v) => v },
  getTextoLista: { type: Function, default: (l, id) => id || '-' },
  formatarMoedaComSigla: { type: Function, default: (v, m) => `${v} ${m}` }
})

// Busca os dados da PO separadamente (o DTO do workflow é agnóstico)
const poDetalhes = ref(null)
const loadingPO = ref(false)

watch(() => props.itemDetalhes?.idPurchaseOrder, async (id) => {
  if (id) {
    loadingPO.value = true
    try {
      const res = await api.get(`/PurchaseOrders/${id}`, { _suppressLoading: true })
      poDetalhes.value = res.data
    } catch (e) {
      poDetalhes.value = null
    } finally {
      loadingPO.value = false
    }
  } else {
    poDetalhes.value = null
  }
}, { immediate: true })
</script>

<template>
  <!-- Resumo em destaque -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">
      <i class="bi bi-chat-quote me-1 text-info"></i> Resumo
    </small>
    <p class="mb-0 text-body-secondary small">{{ itemDetalhes.resumoSolicitacao || 'Nenhum resumo registrado.' }}</p>
    <small v-if="itemDetalhes.nomeUsuarioCriacao" class="text-body-secondary d-block mt-1 small">
      <i class="bi bi-person-fill me-1 text-info"></i> Criado por: <span class="fw-medium">{{ itemDetalhes.nomeUsuarioCriacao }}</span>
    </small>
  </div>

  <!-- Loading -->
  <div v-if="loadingPO" class="text-center py-3">
    <div class="spinner-border spinner-border-sm text-info me-2"></div>
    <span class="text-body-secondary small">Carregando dados da Purchase Order...</span>
  </div>

  <!-- Grid compacto de informações-chave (dados da PO) -->
  <template v-else-if="poDetalhes">
    <div class="row g-2 mb-3">
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">PO #</small>
          <span class="fw-medium text-body-emphasis small">{{ poDetalhes.numeroPurchaseOrder || '#' + poDetalhes.idPurchaseOrder }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Fornecedor</small>
          <span class="fw-medium text-body-emphasis small">{{ poDetalhes.nomeFornecedor || '-' }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Valor Negociado</small>
          <span class="fw-medium text-body-emphasis small">{{ formatarMoedaComSigla(poDetalhes.valorTotalNegociado, poDetalhes.lstMoeda) }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Status</small>
          <span class="badge badge-ghost-info">{{ getTextoLista(listas.LstStatusPurchaseOrder, poDetalhes.lstStatusPurchaseOrder) }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Grupo Aprovador</small>
          <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.nomeGrupoAprovador || '-' }}</span>
        </div>
      </div>
    </div>

    <!-- Produtos com scroll próprio -->
    <div class="mb-2">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <small class="fw-bold text-body-emphasis">
          <i class="bi bi-box-seam me-1 text-info"></i> Produtos ({{ poDetalhes.produtosSolicitados?.length || 0 }})
        </small>
      </div>
      <div v-if="poDetalhes.produtosSolicitados && poDetalhes.produtosSolicitados.length > 0" class="itens-scroll-wrapper border rounded bg-body-tertiary p-1">
        <div
          v-for="(prod, idx) in poDetalhes.produtosSolicitados"
          :key="idx"
          class="d-flex flex-wrap align-items-center gap-2 px-2 py-1 border-bottom border-secondary-subtle item-row"
          :class="{ 'border-bottom-0': idx === poDetalhes.produtosSolicitados.length - 1 }"
        >
          <div class="fw-medium text-body-emphasis" style="min-width: 140px; flex: 1;">
            <span class="small">{{ prod.nomeProduto || prod.sku || 'Produto #' + prod.idProduto }}</span>
            <small v-if="prod.sku && prod.nomeProduto" class="text-body-secondary d-block" style="font-size: 0.7rem;">SKU: {{ prod.sku }}</small>
            <small v-else-if="!prod.sku && !prod.nomeProduto && prod.idProduto" class="text-body-secondary d-block" style="font-size: 0.7rem;">ID: {{ prod.idProduto }}</small>
          </div>
          <div class="text-body-secondary small text-nowrap">
            {{ formatarValor(prod.quantidadeUnFornecedorNegociada) }} {{ getTextoLista(listas.LstUnidadeMedidaFornecedor, prod.lstUnidadeMedidaFornecedor) }}
          </div>
          <div class="text-body-emphasis fw-medium small text-nowrap ms-auto">
            {{ formatarMoedaComSigla(prod.valorTotalNegociado, poDetalhes.lstMoeda) }}
          </div>
        </div>
      </div>
      <div v-else class="text-center text-body-secondary small py-2">
        Nenhum produto registrado.
      </div>
    </div>
  </template>

  <!-- Fallback sem dados da PO -->
  <div v-else class="text-center text-body-secondary small py-3">
    <i class="bi bi-info-circle me-1"></i> Purchase Order não encontrada ou sem dados complementares.
  </div>
</template>

<style scoped>
.itens-scroll-wrapper {
  max-height: 220px;
  overflow-y: auto;
}

.itens-scroll-wrapper::-webkit-scrollbar {
  width: 6px;
}

.itens-scroll-wrapper::-webkit-scrollbar-thumb {
  background-color: var(--bs-border-color);
  border-radius: 3px;
}

.item-row:last-child {
  border-bottom: none !important;
}
</style>
